import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../shared/Icon';
import './OrgTimeZonePrompt.css';

const DISMISS_KEY = 'org_time_zone_prompt_dismissed';

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Asks an administrator to set the organization's time zone when it has none. Until it is
 * set, report periods and the times everyone reads fall back to UTC.
 *
 * Shown only to someone who can open Settings and edit the organization, so it never points
 * at a page the reader cannot use, and not on Settings itself, where the zone field already
 * says no zone is set. Dismissing hides it for the rest of this browser session.
 */
export default function OrgTimeZonePrompt({ me, settingsEnabled }) {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(readDismissed);
  const features = me?.features || {};
  const show = !!me
    && !me.org_primary_time_zone
    && settingsEnabled
    && !!features.can_access_settings
    && !!features.can_edit_organization
    && !pathname.startsWith('/settings')
    && !dismissed;
  if (!show) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Storage blocked: it stays dismissed until the page reloads.
    }
  };

  return (
    <div className="resui-banner resui-banner--alert org-time-zone-prompt" role="status">
      <Icon name="warning" />
      <span className="resui-banner-text">
        Your organization has no time zone set, so times and report periods use UTC.{' '}
        <Link to="/settings">Set it in Settings</Link>.
      </span>
      <button type="button" className="resui-banner-close" onClick={dismiss} aria-label="Dismiss">
        <Icon name="close" />
      </button>
    </div>
  );
}
